import React, { useState, useMemo, useEffect } from "react";
import CreditCardIcon from "@mui/icons-material/CreditCard";
import VerifiedIcon from "@mui/icons-material/Verified";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import NewCardForm from "../../../../JusSpace/components/NewCardForm";
import SelectCardForm from "../../../../JusSpace/components/SelectCardForm";
import { useUserBenefits } from "../../../../Gamification/hooks/useUserBenefits";
import { JUSZAP_PLANS, PLAN_KEYS, resolvePlanKey } from "./plans";
import CheckoutShell from "./CheckoutShell";
import * as S from "./styles";

const formatPrice = (value) => `R$ ${Number(value).toFixed(2).replace(".", ",")}`;

const ModalPayment = ({ onSubmit, isOpen, closeModal, plan, price }) => {
  const [isNewCard, setIsNewCard] = useState(false);
  const { benefitsData } = useUserBenefits();

  // Plano que veio da LP (ou o padrão) apenas pré-seleciona o card.
  const incomingPlanKey = resolvePlanKey(plan, price);
  const [selectedPlanKey, setSelectedPlanKey] = useState(incomingPlanKey);

  useEffect(() => {
    if (isOpen) setSelectedPlanKey(incomingPlanKey);
  }, [isOpen, incomingPlanKey]);

  // Desconto de gamificação aplicável ao JusZap.
  const juszapDiscount = useMemo(() => {
    if (!benefitsData?.benefits) return null;
    return benefitsData.benefits.find((b) => {
      if (b.category !== "discount" || !b.value) return false;
      const labelLower = (b.label || "").toLowerCase();
      const descLower = (b.description || "").toLowerCase();
      return (
        labelLower.includes("whatsapp") ||
        labelLower.includes("juszap") ||
        descLower.includes("whatsapp") ||
        descLower.includes("juszap")
      );
    });
  }, [benefitsData]);

  const discountPercent = juszapDiscount?.value || 0;

  // Só o plano que veio da LP usa o preço informado por ela; o outro card
  // mostra o preço de tabela.
  const basePriceOf = (key) =>
    key === incomingPlanKey && Number(price) > 0
      ? Number(price)
      : JUSZAP_PLANS[key].price;

  const finalPriceOf = (key) =>
    discountPercent > 0
      ? basePriceOf(key) * (1 - discountPercent / 100)
      : basePriceOf(key);

  const selectedPlan = JUSZAP_PLANS[selectedPlanKey];
  const selectedBasePrice = basePriceOf(selectedPlanKey);
  const selectedFinalPrice = finalPriceOf(selectedPlanKey);

  const handleSubmit = (card) => {
    onSubmit(card.hash, selectedPlan.apiPlan);
  };

  return (
    <CheckoutShell isOpen={isOpen} onClose={closeModal}>
      <S.Eyebrow>IA Assistente para WhatsApp</S.Eyebrow>
      <S.Title>
        Transforme seu WhatsApp em um Assistente Jurídico 24h com IA
      </S.Title>

      <S.PlanGrid>
        {PLAN_KEYS.map((key) => {
          const planOption = JUSZAP_PLANS[key];
          const isActive = key === selectedPlanKey;

          return (
            <S.PlanCard
              key={key}
              type="button"
              $active={isActive}
              $hasBadge={Boolean(planOption.highlight)}
              onClick={() => setSelectedPlanKey(key)}
            >
              {planOption.highlight && (
                <S.SaveBadge>{planOption.highlight}</S.SaveBadge>
              )}
              <S.PlanInfo>
                <S.PlanName>{planOption.name}</S.PlanName>
                <S.PlanPrice>
                  {formatPrice(finalPriceOf(key))}/mês{" "}
                  {discountPercent > 0 && (
                    <S.PlanNote>
                      antes {formatPrice(basePriceOf(key))}
                    </S.PlanNote>
                  )}
                </S.PlanPrice>
              </S.PlanInfo>
              <S.Radio $active={isActive} />
            </S.PlanCard>
          );
        })}
      </S.PlanGrid>

      <S.FeaturesList>
        {selectedPlan.features.map((feature) => (
          <S.FeatureItem key={feature}>
            <VerifiedIcon sx={{ fontSize: 16, color: "#01AB7D !important" }} />
            <span>{feature}</span>
          </S.FeatureItem>
        ))}
      </S.FeaturesList>

      {discountPercent > 0 && (
        <S.DiscountBadge>{discountPercent}% OFF Gamificação</S.DiscountBadge>
      )}

      <S.TotalRow>
        <span>Total a pagar</span>
        <S.TotalPrice>
          <strong>{formatPrice(selectedFinalPrice)}</strong>
          {discountPercent > 0 ? (
            <S.OriginalPrice>{formatPrice(selectedBasePrice)}</S.OriginalPrice>
          ) : (
            <S.TotalNote>por mês</S.TotalNote>
          )}
        </S.TotalPrice>
      </S.TotalRow>

      <S.CardFormArea>
        {isNewCard ? (
          <>
            <S.SwitchCardBox onClick={() => setIsNewCard(false)}>
              <CreditCardIcon sx={{ color: "#01AB7D !important" }} />
              <span>Utilizar um cartão já cadastrado</span>
            </S.SwitchCardBox>
            <NewCardForm
              buttonText="Confirmar e pagar"
              onLoading={() => {}}
              onSubmit={handleSubmit}
            />
          </>
        ) : (
          <SelectCardForm
            buttonText="Confirmar e pagar"
            onNewCard={() => setIsNewCard(true)}
            onSubmit={handleSubmit}
          />
        )}
      </S.CardFormArea>

      <S.DisclaimerBox>
        <InfoOutlinedIcon sx={{ fontSize: 16, color: "#5E5E60 !important" }} />
        <S.DisclaimerText>
          O valor do Assistente de IA para WhatsApp será adicionado como cobrança
          separada do seu plano.
        </S.DisclaimerText>
      </S.DisclaimerBox>
    </CheckoutShell>
  );
};

export default ModalPayment;
